import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramCard from '../components/ProgramCard';
import DonationForm from '../components/DonationForm';
import LoadingSpinner from '../components/LoadingSpinner';
import { Program } from '../types';
import { getProgramDetails, getFundraiserDetails } from '../services/api';

const DonatePage: React.FC = () => {
  const { programSlug, programId, fundraiserId } = useParams<{ programSlug?: string; programId?: string; fundraiserId?: string }>();
  const navigate = useNavigate();
  
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch program details
  const [fundraiser, setFundraiser] = React.useState<any | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!programId && !fundraiserId) {
        setError('Program not found');
        setLoading(false);
        return;
      }

      try {
        // If route contains a fundraiserId param (or programSlug === 'fundraiser'), treat as fundraiser page.
        const fundraiserParam = fundraiserId || (programSlug === 'fundraiser' ? programId : undefined);
        if (fundraiserParam) {
          const data = await getFundraiserDetails(fundraiserParam);
          setFundraiser(data.fundraiser || null);
          setProgram(data.program || null);
          // Do NOT rewrite the URL to include a slug — we serve fundraiser pages at /fundraiser/:id
        } else {
          // Regular program page
          const data = await getProgramDetails(programId as string);
          setProgram(data);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load program details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [programId, programSlug]);

  // Handle successful donation
  const handleDonationSuccess = (receiptNumber: string, amount: number) => {
    navigate('/success', { 
      state: { 
        receiptNumber,
        amount,
        programTitle: program?.title,
        organisationName: program?.organisation?.title,
      }
    });
  };

  // Handle donation error
  const handleDonationError = (message: string) => {
    setError(message);
    // Scroll to top to show error
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Loading state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Error state
  if (error && !program) {
    return (
      <div className="page-wrapper">
        <Header />
        <main className="error-page">
          <div className="error-card">
            <div className="error-code">!</div>
            <h1 className="error-title">Oops!</h1>
            <p className="error-message">{error}</p>
            <a
              href="https://givingfortheliving.com"
              className="error-button"
            >
              Return to Home
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Program not found
  if (!program) {
    return (
      <div className="page-wrapper">
        <Header />
        <main className="error-page">
          <div className="error-card">
            <div className="error-code">404</div>
            <h1 className="error-title">Program Not Found</h1>
            <p className="error-message">
              The program you're looking for doesn't exist or has been removed.
            </p>
            <a
              href="https://givingfortheliving.com"
              className="error-button"
            >
              Explore Programs
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Helper to get image URL from media (can be string or object)
  const getMediaUrl = (media: any): string | undefined => {
    if (!media) return undefined;
    if (typeof media === 'string') return media;
    if (media.url) return media.url;
    return undefined;
  };

  const ogImage = program.media && program.media[0] ? getMediaUrl(program.media[0]) : undefined;

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>{program.title} - Donate | Giving For The Living</title>
        <meta name="description" content={program.description?.slice(0, 160)} />
        <meta property="og:title" content={`${program.title} - Donate | Giving For The Living`} />
        <meta property="og:description" content={program.description?.slice(0, 160)} />
        <meta property="og:type" content="website" />
        {ogImage && (
          <meta property="og:image" content={ogImage} />
        )}
      </Helmet>

      <Header />

      <main className="main-content">
        <div className="container">
          {/* Error Alert */}
          {error && (
            <div 
              style={{
                backgroundColor: '#F8D7DA',
                border: '1px solid #F5C6CB',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                color: '#721C24',
              }}
            >
              <strong>Error:</strong> {error}
              <button 
                onClick={() => setError(null)}
                style={{
                  float: 'right',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: '#721C24',
                }}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="donation-layout">
            {/* Program Details */}
            <div className="program-column">
              <ProgramCard program={program} />
            </div>
            
            {/* Donation Form */}
            <div className="donation-column">
              <DonationForm 
                program={program}
                fundRaiseId={fundraiser?._id}
                onSuccess={handleDonationSuccess}
                onError={handleDonationError}
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default DonatePage;
